import { useEffect, useMemo, useState } from "react";
import { Beef, Plus, Trash2 } from "lucide-react";
import { api, money, num, trDate, todayISO } from "../lib/api";
import { PageHeader, Modal, FormField, SubmitButton, useForm, EmptyState, StatMini } from "../components/farm/FarmUI";
import { HERO, HERO_TINT } from "../lib/heroes";

const TYPE_LABEL = {
  count: "Sayım",
  birth: "Doğum",
  death: "Ölüm",
  meat_sale: "Et satışı",
  feed_expense: "Yem gideri",
};

const TYPE_TONE = {
  count: "teal",
  birth: "green",
  death: "red",
  meat_sale: "green",
  feed_expense: "orange",
};

export default function Sheep() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const { form, set, reset } = useForm({ date: todayISO(), type: "count", animal_count: "", amount: "", note: "" });

  const load = () => api.get("/sheep").then(setItems).catch(() => setError("Kayıtlar yüklenemedi."));
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    let animal = 0, births = 0, deaths = 0, sale = 0, feed = 0;
    // Sort ascending by (date, created_at) so same-day records apply in insertion order
    const sorted = [...items].sort((a, b) => {
      const d = (a.date || "").localeCompare(b.date || "");
      if (d !== 0) return d;
      return (a.created_at || "").localeCompare(b.created_at || "");
    });
    for (const s of sorted) {
      if (s.type === "count") animal = Number(s.animal_count) || 0;
      else if (s.type === "birth") { animal += Number(s.animal_count) || 0; births += Number(s.animal_count) || 0; }
      else if (s.type === "death") { animal -= Number(s.animal_count) || 0; deaths += Number(s.animal_count) || 0; }
      else if (s.type === "meat_sale") sale += Number(s.amount) || 0;
      else if (s.type === "feed_expense") feed += Number(s.amount) || 0;
    }
    return { animal: Math.max(0, animal), births, deaths, sale, feed };
  }, [items]);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      date: form.date,
      type: form.type,
      animal_count: Number(form.animal_count) || 0,
      amount: Number(form.amount) || 0,
      note: form.note,
    };
    try {
      const rec = await api.post("/sheep", payload);
      setItems((x) => [rec, ...x].sort((a, b) => (b.date || "").localeCompare(a.date || "")));
      reset();
      setOpen(false);
    } catch {
      setError("Kayıt eklenemedi.");
    }
  };

  const remove = async (id) => {
    try { await api.del(`/sheep/${id}`); setItems((x) => x.filter((i) => i.id !== id)); }
    catch { setError("Silinemedi."); }
  };

  const showsCount = ["count", "birth", "death"].includes(form.type);
  const showsAmount = ["meat_sale", "feed_expense"].includes(form.type);

  return (
    <>
      <PageHeader
        eyebrow="KÜÇÜKBAŞ / ET İŞİ"
        title="Küçükbaş kayıtları"
        subtitle="Hayvan sayısı, doğum-ölüm, et satışları ve yem giderleri."
        heroImage={HERO.sheep}
        heroTint={HERO_TINT.sheep}
        actions={
          <button className="primary-btn" data-testid="sheep-add-btn" onClick={() => setOpen(true)}>
            <Plus size={17} /> Yeni hareket
          </button>
        }
      />

      {error && <div className="api-error" data-testid="sheep-error">{error}</div>}

      <section className="stats-mini-row">
        <StatMini label="Mevcut hayvan" value={`${stats.animal}`} tone="teal" testId="sheep-stat-animal" />
        <StatMini label="Doğum" value={`${stats.births}`} tone="green" testId="sheep-stat-birth" />
        <StatMini label="Ölüm" value={`${stats.deaths}`} tone="red" testId="sheep-stat-death" />
        <StatMini label="Et satışı" value={money(stats.sale)} tone="green" testId="sheep-stat-sale" />
        <StatMini label="Yem gideri" value={money(stats.feed)} tone="orange" testId="sheep-stat-feed" />
      </section>

      <div className="panel">
        {items.length === 0 ? (
          <EmptyState icon={Beef} title="Henüz kayıt yok" text="İlk olarak mevcut hayvan sayınızı ekleyin, sonra doğum-ölüm ve satışları işleyin." actionLabel="Sayım ekle" onAction={() => setOpen(true)} testId="sheep-empty-add" />
        ) : (
          <div className="sheep-list">
            {items.map((s) => (
              <div className="sheep-row" key={s.id} data-testid={`sheep-item-${s.id}`}>
                <div className={`sheep-tag tone-${TYPE_TONE[s.type]}`}>{TYPE_LABEL[s.type]}</div>
                <div className="sheep-body">
                  <b>
                    {["count", "birth", "death"].includes(s.type)
                      ? `${s.animal_count} hayvan`
                      : money(s.amount)}
                  </b>
                  <small>{trDate(s.date)} {s.note ? `· ${s.note}` : ""}</small>
                </div>
                <button className="delete-btn always" data-testid={`sheep-delete-${s.id}`} onClick={() => remove(s.id)}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {open && (
        <Modal title="Küçükbaş hareketi" subtitle="KÜÇÜKBAŞ" onClose={() => setOpen(false)}>
          <form onSubmit={submit}>
            <FormField label="Hareket türü">
              <select required data-testid="sheep-form-type" value={form.type} onChange={(e) => set("type", e.target.value)}>
                <option value="count">Sayım (Mevcut hayvan)</option>
                <option value="birth">Doğum</option>
                <option value="death">Ölüm</option>
                <option value="meat_sale">Et satışı</option>
                <option value="feed_expense">Yem gideri</option>
              </select>
            </FormField>
            <FormField label="Tarih">
              <input required type="date" data-testid="sheep-form-date" value={form.date} onChange={(e) => set("date", e.target.value)} />
            </FormField>
            {showsCount && (
              <FormField label="Hayvan sayısı">
                <input required type="number" step="1" data-testid="sheep-form-count" value={form.animal_count} onChange={(e) => set("animal_count", e.target.value)} />
              </FormField>
            )}
            {showsAmount && (
              <FormField label="Tutar (₺)">
                <input required type="number" step="1" data-testid="sheep-form-amount" value={form.amount} onChange={(e) => set("amount", e.target.value)} />
              </FormField>
            )}
            <FormField label="Not">
              <textarea data-testid="sheep-form-note" placeholder="Açıklama" value={form.note} onChange={(e) => set("note", e.target.value)} />
            </FormField>
            <SubmitButton testId="sheep-form-submit">Kaydet</SubmitButton>
          </form>
        </Modal>
      )}
    </>
  );
}
